import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type {
  DateFilter,
  KindFilter,
  LensFilter,
} from './constants';

export interface FilterState {
  lensId: LensFilter['id'];
  kindId: string;
  dateId: string;
  query: string;
}

interface TimelineFilterStripProps {
  lensFilters: LensFilter[];
  kindFilters: KindFilter[];
  dateFilters: DateFilter[];
  kindCounts: Record<string, number>;
  state: FilterState;
  totalShown: number;
  onChange: (next: FilterState) => void;
}

/**
 * Three-row filter strip:
 *
 * 1. Lens — `all` / `clinical` / `operational` / `admin` (server-side
 *    envelope `group` filter; the server pulls in encounter roots when
 *    a child passes — see GDN-938 §6).
 * 2. Type — doc_kind grouping (currently only meaningful within the
 *    document kind; future kinds will need their own subkind groupings).
 * 3. When — date buckets + search field.
 *
 * Counts shown alongside active kind filters; "Clear" pill appears
 * whenever any non-default filter is set.
 */
export function TimelineFilterStrip({
  lensFilters,
  kindFilters,
  dateFilters,
  kindCounts,
  state,
  totalShown,
  onChange,
}: TimelineFilterStripProps) {
  const isFiltered =
    state.lensId !== 'all' ||
    state.kindId !== 'all' ||
    state.dateId !== 'all' ||
    state.query !== '';

  const setLens = (id: LensFilter['id']) => onChange({ ...state, lensId: id });
  const setKind = (id: string) => onChange({ ...state, kindId: id });
  const setDate = (id: string) => onChange({ ...state, dateId: id });
  const setQuery = (q: string) => onChange({ ...state, query: q });
  const clearAll = () =>
    onChange({ lensId: 'all', kindId: 'all', dateId: 'all', query: '' });

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        sx={{ rowGap: 0.5 }}
      >
        <GroupLabel>Lens</GroupLabel>
        {lensFilters.map(f => {
          const active = state.lensId === f.id;
          return (
            <Chip
              key={f.id}
              size="small"
              label={f.label}
              color={active ? 'primary' : 'default'}
              variant={active ? 'filled' : 'outlined'}
              onClick={() => setLens(f.id)}
            />
          );
        })}
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        sx={{ rowGap: 0.5 }}
      >
        <GroupLabel>Type</GroupLabel>
        {kindFilters.map(f => {
          const active = state.kindId === f.id;
          const count = kindCounts[f.id] ?? 0;
          return (
            <Chip
              key={f.id}
              size="small"
              color={active ? 'primary' : 'default'}
              variant={active ? 'filled' : 'outlined'}
              onClick={() => setKind(f.id)}
              label={
                count > 0 ? (
                  <Box
                    component="span"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                  >
                    {f.label}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ opacity: 0.75, fontVariantNumeric: 'tabular-nums' }}
                    >
                      {count}
                    </Typography>
                  </Box>
                ) : (
                  f.label
                )
              }
            />
          );
        })}
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        sx={{ rowGap: 0.5 }}
      >
        <GroupLabel>When</GroupLabel>
        {dateFilters.map(f => {
          const active = state.dateId === f.id;
          return (
            <Chip
              key={f.id}
              size="small"
              label={f.label}
              color={active ? 'primary' : 'default'}
              variant={active ? 'filled' : 'outlined'}
              onClick={() => setDate(f.id)}
            />
          );
        })}
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="Search…"
          value={state.query}
          onChange={e => setQuery(e.target.value)}
          sx={{ minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment: state.query ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {isFiltered && (
          <Chip
            size="small"
            variant="outlined"
            onClick={clearAll}
            label={`Clear · ${totalShown}`}
            sx={{ fontWeight: 500 }}
          />
        )}
      </Stack>
    </Stack>
  );
}

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ fontSize: 10, letterSpacing: 1, pr: 0.5, minWidth: 48 }}
    >
      {children}
    </Typography>
  );
}
